<script setup>
import {computed} from "vue";
import {Coins, Ticket, Wallet} from "lucide-vue-next";
import {useSessionStore} from "@/utils/useSessionStore.js";
import {useNotification} from "@/utils/useNotification.js";
import {useRouter} from "vue-router";
const router = useRouter();
import {$confirm} from "@/utils/pluginDialog.js";
import {$t} from "@/i18n/useLanguage.js";

const sessionStore = useSessionStore();
const user = computed(() => sessionStore.getUser || {});
const visibleMemberTags = computed(() => user.value?.visible_member_tags || []);
const emit = defineEmits(['confirm-logout'])
const getNumberFormat = (number) => {
  if (!number) return '0';
  if (typeof number === 'string') number = parseInt(number);
  let num = number.toString();
  const pattern = /(-?\d+)(\d{3})/;
  while (pattern.test(num)) {
    num = num.replace(pattern, "$1,$2");
  }
  return num;
}

const goTo = (path) => {
  router.push(path);
}

const notification = useNotification();

async function confirmLogout() {
  try {
    emit('confirm-logout')
    await $confirm($t('로그아웃 하시겠습니까?'))
    await sessionStore.logout();
    await router.push('/')
  } catch (e) {
    //
  }

}
notification.get();

</script>
<template>
  <div class="w-full px-4 pt-4 pb-8 bg-stone-700 flex-col justify-end items-start flex">
    <div
      v-if="!sessionStore.authenticated"
      class="flex-col justify-end items-start gap-3 flex mx-auto max-w-screen-xl lg:px-4 w-full"
    >
      <button
        class="w-full text-left border border-white/30 px-4 py-4 text-white"
        @click="()=>{router.push('/login')}"
      >
        <div class="ev-heading-2 !text-white">
          {{ $t("로그인 하세요.") }}
        </div>
        <div class="mt-2 text-sm text-stone-300">
          {{ $t("클릭하여 로그인 페이지로 이동합니다.") }}
        </div>
      </button>
    </div>
    <div class="flex-col justify-end items-start gap-3 flex mx-auto max-w-screen-xl lg:px-4 w-full">
      <template v-if="sessionStore.authenticated">
        <div class="justify-end w-full items-end gap-4 flex">
          <router-link to="/my-page/account">
            <img src="/images/icons/cog.svg">
          </router-link>
          <router-link to="/my-page/notification">
            <div class="relative">
              <div
                v-if="notification.getUnreadCount"
                class="w-4 h-4 -right-1 -top-1 text-white text-xs bg-red-400 rounded-full justify-center items-center flex absolute"
              >
                {{ notification.getUnreadCount }}
              </div>
              <img src="/images/icons/bell.svg">
            </div>
          </router-link>
        </div>
        <div class="w-full grow shrink basis-0 justify-start items-end gap-3 inline-flex">
          <div class="justify-center items-center gap-2 flex">
            <div class="flex items-center gap-1">
              <h4
                class="ev-heading-1 cursor-pointer"
                style="color: white"
                @click="()=>{router.push('/my-page')}"
              >
                {{ $t('{name}님', {name: user.mb_name}) }}
              </h4>
              <span class="text-white text-xs leading-3">
                ( @{{ user.mb_id }} )
              </span>
            </div>
            <div class="flex rounded-full border-white border px-2 py-1">
              <div class="text-center text-white text-xs font-semibold leading-3">
                {{ $t(user.my_group) }} {{ $t("회원") }}
              </div>
            </div>
            <div
              v-if="visibleMemberTags.length"
              class="flex flex-wrap items-center gap-1"
            >
              <span
                v-for="tag in visibleMemberTags"
                :key="tag.id"
                class="inline-flex max-w-[140px] items-center rounded-full border border-white/70 px-2 py-1 text-xs font-semibold leading-3 text-white"
              >
                {{ tag.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex w-full flex-wrap items-center justify-between gap-2">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <button
              type="button"
              class="summary-button"
              :aria-label="$t('포인트 내역 보기')"
              @click="goTo('/my-page/point')"
            >
              <Coins
                class="summary-button-icon"
                aria-hidden="true"
              />
              {{ $t("포인트 {point} 점", {point:getNumberFormat(user.available_point)}) }}
            </button>
            <button
              type="button"
              class="summary-button"
              :aria-label="$t('적립금 내역 보기')"
              @click="goTo('/my-page/point#credit')"
            >
              <Wallet
                class="summary-button-icon"
                aria-hidden="true"
              />
              {{ $t("적립금 {credit} 원", {credit:getNumberFormat(user.available_cash)}) }}
            </button>
            <button
              type="button"
              class="summary-button"
              :aria-label="$t('쿠폰 목록 보기')"
              @click="goTo('/my-page/coupon')"
            >
              <Ticket
                class="summary-button-icon"
                aria-hidden="true"
              />
              {{ $t("쿠폰 {count} 개", {count:sessionStore.getCouponCount}) }}
            </button>
          </div>
          <div
            class="text-white text-xs ml-auto border border-white px-2 py-0.5 rounded-full cursor-pointer"
            @click="confirmLogout"
          >
            {{ $t("로그아웃") }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.summary-button {
  @apply inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/60 bg-white/10 px-3 text-sm font-semibold leading-none text-white transition hover:border-white hover:bg-white hover:text-stone-800 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-stone-700
}

.summary-button-icon {
  @apply h-4 w-4 shrink-0
}
</style>
